import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import WidgetCard from './WidgetCard'
import { GhostButton, PrimaryButton } from '../../../layout/styledComponents'
import { usePatient } from '../../../context/PatientContext'
import * as shopSvc from '../../../services/shopService'
import { parseUtcDate } from '../../../utils/dates'

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const SessionRow = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 14px 16px;
  border-radius: 14px;
  background: ${(p) => p.theme?.color?.bgRaised || '#fff'};
  border: 1px solid ${(p) => p.theme?.color?.urgent || '#C24D49'};
  flex-wrap: wrap;
`

const Detail = styled.div`
  flex: 1;
  min-width: 0;
`

const Title = styled.div`
  font-weight: 600;
  font-size: 15px;
  color: ${(p) => p.theme?.color?.ink || '#1F2937'};
`

const Sub = styled.div`
  font-size: 12px;
  color: ${(p) => p.theme?.color?.inkMuted || '#6B7280'};
  margin-top: 2px;
`

const Reason = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: ${(p) => p.theme?.color?.urgent || '#C24D49'};
  margin-top: 4px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`

const Actions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`

const PayBtn = styled(PrimaryButton)`
  background: ${(p) => p.theme?.color?.urgent || '#C24D49'};
  &:hover { background: ${(p) => p.theme?.color?.urgent || '#C24D49'}; opacity: 0.9; }
`

function formatAmount(minor, currency = 'INR') {
  const major = (minor || 0) / 100
  if (currency === 'INR') return `₹${major.toLocaleString('en-IN')}`
  if (currency === 'USD') return `$${major.toLocaleString('en-US', { minimumFractionDigits: 2 })}`
  return `${currency} ${major}`
}

function whenLabel(scheduledAt) {
  if (!scheduledAt) return null
  const d = parseUtcDate(scheduledAt)
  const now = new Date()
  const sameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  const tomorrow = parseUtcDate(now.getTime() + 86400000)
  const datePart = sameDay(d, now)
    ? 'Today'
    : sameDay(d, tomorrow)
      ? 'Tomorrow'
      : d.toLocaleDateString([], { month: 'short', day: 'numeric' })
  const timePart = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
  return `${datePart} · ${timePart}`
}

/**
 * Lists every booked session that is *blocked on payment*. The Pay button
 * triggers the patient-portal gateway handoff: backend POST returns a
 * Razorpay order envelope, the modal opens, and on success we refresh
 * Today so the row drops off (or re-renders as joinable).
 *
 * <p>Source: union of {@code data.nextSession} and
 * {@code data.upcomingSessions} filtered by {@code paymentBlocked === true}.
 * Card auto-hides when nothing is blocked.</p>
 */
export default function BlockedSessionsCard({ nextSession, upcomingSessions, onPaid }) {
  const { currentProfile } = usePatient()
  const navigate = useNavigate()
  const [paying, setPaying] = useState(null)
  const [error, setError] = useState(null)

  const blocked = []
  const seen = new Set()
  const collect = (s) => {
    if (!s || !s.paymentBlocked || !s.eventId) return
    if (seen.has(s.eventId)) return
    seen.add(s.eventId)
    blocked.push(s)
  }
  collect(nextSession)
  ;(upcomingSessions || []).forEach(collect)
  if (blocked.length === 0) return null

  blocked.sort((a, b) => {
    const at = a.scheduledAt ? parseUtcDate(a.scheduledAt).getTime() : 0
    const bt = b.scheduledAt ? parseUtcDate(b.scheduledAt).getTime() : 0
    return at - bt
  })

  async function pay(s) {
    if (!s.paymentDueId) return
    setPaying(s.eventId)
    setError(null)
    try {
      await shopSvc.payDue(currentProfile, s.paymentDueId, {
        onSuccess: () => {
          if (typeof onPaid === 'function') onPaid()
          else window.location.reload()
        },
      })
    } catch (e) {
      setError(e?.message || 'Could not start payment. Please try again.')
    } finally {
      setPaying(null)
    }
  }

  const totalMinor = blocked.reduce((sum, s) => sum + (s.paymentAmountMinor || 0), 0)
  const headlineCurrency = blocked[0]?.paymentCurrency || 'INR'
  const badge = blocked.length > 1
    ? `${blocked.length} sessions · ${formatAmount(totalMinor, headlineCurrency)} total`
    : undefined

  return (
    <WidgetCard tone="urgent" title="Pay to confirm" badge={badge}>
      <List>
        {blocked.map((s) => (
          <SessionRow key={s.eventId}>
            <Detail>
              <Title>{s.title || 'Session'}</Title>
              <Sub>
                {s.practitionerName}
                {s.scheduledAt ? ` · ${whenLabel(s.scheduledAt)}` : ''}
              </Sub>
              <Reason>Awaiting payment</Reason>
            </Detail>
            <Actions>
              <PayBtn
                onClick={() => pay(s)}
                disabled={paying === s.eventId}
              >
                {paying === s.eventId
                  ? 'Opening…'
                  : s.paymentAmountMinor && s.paymentAmountMinor > 0
                    ? `Pay ${formatAmount(s.paymentAmountMinor, s.paymentCurrency)}`
                    : 'Pay'}
              </PayBtn>
              <GhostButton
                onClick={() => navigate(`/patient-home/sessions/${s.eventId}`)}
              >
                Details
              </GhostButton>
            </Actions>
          </SessionRow>
        ))}
      </List>
      {error && (
        <div style={{ marginTop: 10, color: '#C24D49', fontSize: 13 }}>{error}</div>
      )}
    </WidgetCard>
  )
}
